import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, AlertTriangle, Sparkles, Loader2, ChevronRight, ChevronLeft, Briefcase, GraduationCap, FileText, Settings, DollarSign, Lock, Coins, ArrowUpRight } from 'lucide-react';
import SarSymbol from '@/components/SarSymbol';
import AedSymbol from '@/components/AedSymbol';
import { supabase } from '@/integrations/supabase/client';
import { logConsentAcknowledgement } from '@/lib/consentLogger';
import { useCreateJob, useJobs } from '@/hooks/useJobs';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useOpeningsLimit } from '@/hooks/useOpeningsLimit';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { shouldBlockJobCreation, shouldBlockJobPublish } from '@/lib/forkhire/jobLifecycleGovernance';
import { z } from 'zod';
import { useForm, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  type JobValidationField,
  buildJobValidationMessage,
  validateJobForPublish,
} from '@/lib/jobPublishValidation';
import {
  buildCreateJobDraftStorageKey,
  clearCreateJobDraft,
  hasMeaningfulCreateJobDraftContent,
  readCreateJobDraft,
  writeCreateJobDraft,
  type CreateJobDraftState,
} from '@/lib/forkhire/createJobDraftPersistence';

const TagInput = ({ label, placeholder, value, onChange, onAdd, tags, onRemove, addLabel = 'Add'
}: {label: string;placeholder: string;value: string;onChange: (v: string) => void;onAdd: () => void;tags: string[];onRemove: (i: number) => void;addLabel?: string;}) =>
<div>
    <label className="text-sm font-medium text-foreground">{label}</label>
    <div className="flex gap-2 mt-1.5">
      <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      onKeyDown={(e) => {if (e.key === 'Enter') {e.preventDefault();onAdd();}}}
      className="bg-muted/50" />
    
      <Button type="button" variant="outline" size="sm" onClick={onAdd} className="shrink-0 px-3">{addLabel}</Button>
    </div>
    {tags.length > 0 &&
  <div className="flex flex-wrap gap-1.5 mt-2">
        {tags.map((t, i) =>
    <Badge key={i} variant="secondary" className="gap-1 text-xs">
            {t} <X className="w-3 h-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => onRemove(i)} />
          </Badge>
    )}
      </div>
  }
  </div>;


const buildJobSchema = (t: TFunction) => z.object({
  title: z.string().min(2, t('hire.createJob.validation.titleRequired')).max(200),
  department: z.string().min(1, t('hire.createJob.validation.departmentRequired')).max(100),
  location: z.string().min(1, t('hire.createJob.validation.locationRequired')).max(200),
  job_type: z.string().min(1, t('hire.createJob.validation.jobTypeRequired')),
  employment_type: z.string().min(1, t('hire.createJob.validation.employmentTypeRequired')),
  seniority: z.string().min(1, t('hire.createJob.validation.seniorityRequired')),
  min_experience_years: z.string().min(1, t('hire.createJob.validation.minimumExperienceRequired')).refine((value) => {
    const num = Number(value);
    return !Number.isNaN(num) && num >= 0 && num <= 50;
  }, t('hire.createJob.validation.experienceRange')),
  education_level: z.string().min(1, t('hire.createJob.validation.educationRequired')),
  notice_period: z.coerce.number().int(t('hire.createJob.validation.wholeNumber')).min(1, t('hire.createJob.validation.minimumOneDay')).optional().or(z.literal('')),
  start_date: z.string().refine((value) => {
    if (!value) return true;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, t('hire.createJob.validation.invalidStartDate')).optional().or(z.literal('')),
  salary_min: z.coerce.number().min(0).optional().or(z.literal('')),
  salary_max: z.coerce.number().min(0).optional().or(z.literal('')),
  salary_currency: z.string().default('SAR'),
  description: z.string().min(1, t('hire.createJob.validation.descriptionRequired')).max(10000),
  requirements: z.string().min(1, t('hire.createJob.validation.requirementsRequired')).max(10000),
  cv_pass_threshold: z.coerce.number().min(0).max(100).default(80),
  interview_pass_threshold: z.coerce.number().min(1, t('hire.createJob.validation.interviewScoreRequired')).max(100).default(70),
  open_duration_days: z.coerce.number().int({ message: t('hire.createJob.validation.wholeNumber') }).min(1, t('hire.createJob.validation.minimumOneDay')).max(90, t('hire.createJob.validation.maximum90Days')),
  visibility_mode: z.string().default('direct_link_only'),
  number_of_openings: z.coerce.number().int(t('hire.createJob.validation.wholeNumber')).min(1, t('hire.createJob.validation.atLeastOneOpening')).max(500, t('hire.createJob.validation.maximum500')).default(1)
});

type JobFormValues = z.infer<ReturnType<typeof buildJobSchema>>;

const DEFAULT_JOB_FORM_VALUES: JobFormValues = {
  title: '',
  department: '',
  location: '',
  job_type: 'onsite',
  employment_type: 'full-time',
  seniority: '',
  min_experience_years: '',
  education_level: '',
  notice_period: '',
  start_date: '',
  salary_min: '',
  salary_max: '',
  salary_currency: 'SAR',
  description: '',
  requirements: '',
  cv_pass_threshold: 80,
  open_duration_days: 30,
  interview_pass_threshold: 70,
  visibility_mode: 'direct_link_only',
  number_of_openings: 1,
};

const isJobFormField = (field: string): field is FieldPath<JobFormValues> =>
  Object.prototype.hasOwnProperty.call(DEFAULT_JOB_FORM_VALUES, field);

const STEP_KEYS = [
  { id: 0, key: 'stepRole', icon: Briefcase },
  { id: 1, key: 'stepQualifications', icon: GraduationCap },
  { id: 2, key: 'stepSkills', icon: FileText },
  { id: 3, key: 'stepSettings', icon: Settings },
] as const;

export function CreateJobDialog() {
  const { t } = useTranslation();
  const { profile, session } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const [step, setStep] = useState(0);
  const createJob = useCreateJob();
  const { data: existingJobs } = useJobs({ refetchOnWindowFocus: !open });
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [certifications, setCertifications] = useState<string[]>([]);
  const [certInput, setCertInput] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [langInput, setLangInput] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [benefitInput, setBenefitInput] = useState('');
  const [pendingAction, setPendingAction] = useState<'draft' | 'publish' | null>(null);
  const [publishValidationFields, setPublishValidationFields] = useState<JobValidationField[]>([]);
  const [publishDraftJobId, setPublishDraftJobId] = useState<string | null>(null);
  const [publishIdempotencyKey, setPublishIdempotencyKey] = useState<string | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const { hasAccess: hasAiJobGen } = useServiceAccess(SERVICE_KEYS.AI_JOB_GENERATOR);
  const jobEntitlement = useEntitlement(SERVICE_KEYS.JOB_MANAGEMENT);
  const aiGenEntitlement = useEntitlement(SERVICE_KEYS.AI_JOB_GENERATOR);
  const [responsibilities, setResponsibilities] = useState('');
  const [responsibilitiesError, setResponsibilitiesError] = useState('');
  const aiGenerationAttemptRef = useRef<{ fingerprint: string; idempotencyKey: string } | null>(null);
  const aiGenerateInFlightRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const restoredDraftKeyRef = useRef<string | null>(null);
  const draftStorageKey = useMemo(() => {
    if (!profile?.org_id || !session?.user?.id) {
      return null;
    }

    return buildCreateJobDraftStorageKey({ orgId: profile.org_id, userId: session.user.id });
  }, [profile?.org_id, session?.user?.id]);

  const form = useForm<JobFormValues>({
    resolver: zodResolver(buildJobSchema(t)),
    defaultValues: DEFAULT_JOB_FORM_VALUES
  });

  const getDraftState = useCallback((
    formValues: Partial<JobFormValues> = form.getValues(),
    databaseDraftJobId: string | null = publishDraftJobId,
    idempotencyKey: string | null = publishIdempotencyKey,
  ): CreateJobDraftState => ({
    step,
    formValues,
    databaseDraftJobId,
    publishIdempotencyKey: idempotencyKey,
    skills,
    skillInput,
    certifications,
    certInput,
    languages,
    langInput,
    benefits,
    benefitInput,
    responsibilities,
    pendingAction,
    duplicateWarning,
  }), [
    benefitInput,
    benefits,
    certInput,
    certifications,
    duplicateWarning,
    form,
    langInput,
    languages,
    pendingAction,
    publishDraftJobId,
    publishIdempotencyKey,
    responsibilities,
    skillInput,
    skills,
    step,
  ]);

  const resetDraftState = useCallback(() => {
    form.reset(DEFAULT_JOB_FORM_VALUES);
    setStep(0);
    setSkills([]);
    setSkillInput('');
    setCertifications([]);
    setCertInput('');
    setLanguages([]);
    setLangInput('');
    setBenefits([]);
    setBenefitInput('');
    setResponsibilities('');
    setResponsibilitiesError('');
    setPublishValidationFields([]);
    setDuplicateWarning(false);
    setPendingAction(null);
    setPublishDraftJobId(null);
    setPublishIdempotencyKey(null);
    setRestoredDraft(false);
    aiGenerationAttemptRef.current = null;
  }, [form]);

  const safelyClearStoredDraft = useCallback(() => {
    if (!draftStorageKey) {
      return;
    }

    try {
      clearCreateJobDraft(window.sessionStorage, draftStorageKey);
    } catch {
      // Browser storage can be unavailable in private/restricted contexts.
    }
  }, [draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey) {
      restoredDraftKeyRef.current = null;
      return;
    }

    if (restoredDraftKeyRef.current === draftStorageKey) return;
    restoredDraftKeyRef.current = draftStorageKey;
    try {
      const draft = readCreateJobDraft(window.sessionStorage, draftStorageKey);
      if (!draft) return;
      form.reset({ ...DEFAULT_JOB_FORM_VALUES, ...draft.formValues } as JobFormValues);
      setStep(draft.step);
      setSkills(draft.skills);
      setSkillInput(draft.skillInput);
      setCertifications(draft.certifications);
      setCertInput(draft.certInput);
      setLanguages(draft.languages);
      setLangInput(draft.langInput);
      setBenefits(draft.benefits);
      setBenefitInput(draft.benefitInput);
      setResponsibilities(draft.responsibilities);
      setPublishDraftJobId(draft.databaseDraftJobId ?? null);
      setPublishIdempotencyKey(draft.publishIdempotencyKey ?? null);
      setPendingAction(draft.pendingAction);
      setDuplicateWarning(draft.duplicateWarning);
      setRestoredDraft(true);
    } catch {
      // Keep the dialog usable even when browser storage is blocked.
    }
  }, [draftStorageKey, form]);

  const persistDraft = useCallback((
    formValues: Partial<JobFormValues> = form.getValues(),
    databaseDraftJobId: string | null = publishDraftJobId,
    idempotencyKey: string | null = publishIdempotencyKey,
  ) => {
    if (!open || !draftStorageKey) return;
    try {
      writeCreateJobDraft(window.sessionStorage, draftStorageKey, getDraftState(formValues, databaseDraftJobId, idempotencyKey));
    } catch {
      // Draft persistence is best-effort only; form state remains in memory.
    }
  }, [draftStorageKey, form, getDraftState, open, publishDraftJobId, publishIdempotencyKey]);

  useEffect(() => {
    persistDraft();
  }, [persistDraft]);

  useEffect(() => {
    const subscription = form.watch((values) => persistDraft(values as Partial<JobFormValues>));
    return () => subscription.unsubscribe();
  }, [form, persistDraft]);

  const requestClose = useCallback(() => {
    if (createJob.isPending) return;
    if (hasMeaningfulCreateJobDraftContent(getDraftState())) {
      setDiscardConfirmOpen(true);
      return;
    }
    safelyClearStoredDraft();
    resetDraftState();
    setOpen(false);
  }, [createJob.isPending, getDraftState, resetDraftState, safelyClearStoredDraft]);

  const confirmDiscardDraft = useCallback(() => {
    safelyClearStoredDraft();
    resetDraftState();
    setDiscardConfirmOpen(false);
    setOpen(false);
  }, [resetDraftState, safelyClearStoredDraft]);

  const selectedSeniority = form.watch('seniority');
  const { data: maxOpeningsLimit } = useOpeningsLimit(selectedSeniority);
  const aiGenerateBlocked = aiGenerating || !hasAiJobGen || aiGenEntitlement.isLoading || !aiGenEntitlement.allowed;
  const aiGenerateTitle = !hasAiJobGen
    ? t('hire.createJob.aiGeneratorNotIncluded')
    : !aiGenEntitlement.allowed
      ? t('hire.createJob.aiGeneratorLimitReached')
      : undefined;
  const aiGenerateStatusText = aiGenEntitlement.isLoading
    ? t('hire.createJob.checkingAiAllowance')
    : aiGenEntitlement.isUsingCredits
      ? t('hire.createJob.aiUsesCredits', { credits: aiGenEntitlement.creditBalance })
      : !aiGenEntitlement.allowed
        ? t('hire.createJob.aiNotAvailableWithCredits', { credits: aiGenEntitlement.creditBalance })
        : aiGenEntitlement.isUnlimited
          ? t('hire.createJob.aiAvailableUnlimited')
          : t('hire.createJob.aiUsesRemaining', { count: aiGenEntitlement.remaining });

  const handleAiGenerate = async () => {
    if (aiGenerateInFlightRef.current) return;
    const title = form.getValues('title');
    if (!title || title.trim().length < 2) {
      form.setError('title', { message: t('hire.createJob.enterTitleForAi') });
      return;
    }
    if (aiGenEntitlement.isLoading || !hasAiJobGen || !aiGenEntitlement.allowed) {
      form.setError('title', { message: t('hire.createJob.aiUnavailableForSubscription') });
      return;
    }
    aiGenerateInFlightRef.current = true;
    setAiGenerating(true);
    // Log consent for AI job detail generation (fire-and-forget)
    logConsentAcknowledgement('cv_scoring', null, 'hire', {
      consent_categories: ['ai_processing'],
      source_page: '/jobs/create',
      action: 'generate_job_details',
    }).catch(() => {});
    try {
      const generationFingerprint = JSON.stringify({
        title: title.trim(),
        seniority: form.getValues('seniority') || null,
        employment_type: form.getValues('employment_type') || null,
        job_type: form.getValues('job_type') || null,
        location: form.getValues('location')?.trim() || null,
        org_id: profile?.org_id || null,
      });
      if (aiGenerationAttemptRef.current?.fingerprint !== generationFingerprint) {
        aiGenerationAttemptRef.current = {
          fingerprint: generationFingerprint,
          idempotencyKey: crypto.randomUUID(),
        };
      }
      const creditMarketplaceAttemptId = aiGenerationAttemptRef.current.idempotencyKey;
      const { data, error } = await supabase.functions.invoke('generate-job-details', {
        body: {
          title: title.trim(),
          seniority: form.getValues('seniority') || undefined,
          employment_type: form.getValues('employment_type') || undefined,
          job_type: form.getValues('job_type') || undefined,
          location: form.getValues('location')?.trim() || undefined,
          org_id: profile?.org_id || undefined,
          idempotency_key: creditMarketplaceAttemptId,
        }
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.description) form.setValue('description', data.description, { shouldValidate: true });
      if (data?.responsibilities) setResponsibilities(data.responsibilities);
      if (data?.requirements) form.setValue('requirements', data.requirements, { shouldValidate: true });
      if (data?.min_experience_years != null) form.setValue('min_experience_years', String(data.min_experience_years), { shouldValidate: true });
      if (data?.education_level) form.setValue('education_level', data.education_level, { shouldValidate: true });
      if (data?.seniority) form.setValue('seniority', data.seniority, { shouldValidate: true });
      if (data?.department) form.setValue('department', data.department, { shouldValidate: true });
      if (data?.location) form.setValue('location', data.location, { shouldValidate: true });
      if (data?.skills && Array.isArray(data.skills)) {
        setSkills((prev) => Array.from(new Set([...prev, ...data.skills])));
      }
      if (data?.certifications && Array.isArray(data.certifications)) {
        setCertifications((prev) => Array.from(new Set([...prev, ...data.certifications])));
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['entitlement', profile?.org_id, SERVICE_KEYS.AI_JOB_GENERATOR], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['forkhire-universal-wallet', profile?.org_id], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['forkhire-universal-credit-ledger', profile?.org_id], refetchType: 'all' }),
      ]);
      aiGenerationAttemptRef.current = null;
    } catch (err: unknown) {
      console.error('AI generation failed:', err);
      form.setError('title', { message: t('hire.createJob.aiGenerationFailed') });
    } finally {
      aiGenerateInFlightRef.current = false;
      setAiGenerating(false);
    }
  };

  const addTag = (value: string, list: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, inputSetter: React.Dispatch<React.SetStateAction<string>>) => {
    const trimmed = value.trim();
    if (trimmed && !list.includes(trimmed)) setter([...list, trimmed]);
    inputSetter('');
  };

  const removeTag = (index: number, list: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(list.filter((_, i) => i !== index));
  };

  const step0Fields = ['title', 'department', 'location', 'job_type', 'employment_type', 'seniority'] as const;
  const step1Fields = ['description', 'requirements'] as const;
  const step2Fields = ['min_experience_years', 'education_level'] as const;
  const step3Fields = ['cv_pass_threshold', 'interview_pass_threshold', 'open_duration_days'] as const;

  const validateStep = async (s: number) => {
    const fieldsMap = [step0Fields, step1Fields, step2Fields, step3Fields] as const;
    const fields = fieldsMap[s];
    if (!fields) return false;
    const result = await form.trigger([...fields]);
    // Extra validation for responsibilities in step 1
    if (s === 1) {
      if (!responsibilities.trim()) {
        setResponsibilitiesError(t('hire.createJob.responsibilitiesRequired'));
        return false;
      }
      setResponsibilitiesError('');
    }
    return result;
  };

  const goNext = async () => {
    const valid = await validateStep(step);
    if (valid) setStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const applyPublishValidation = (fields: JobValidationField[]) => {
    setPublishValidationFields(fields);
    fields.forEach((field) => {
      if (field.field === 'responsibilities') {
        setResponsibilitiesError(field.message);
        return;
      }
      if (isJobFormField(field.field)) {
        form.setError(field.field, { message: field.message });
      }
    });
    const firstStep = fields[0]?.step;
    if (firstStep) {
      setStep(STEP_KEYS.find((item) => item.key.toLowerCase().includes(firstStep))?.id ?? 0);
    }
    toast({
      title: t('hire.createJob.cannotPublishJob'),
      description: buildJobValidationMessage(fields),
      variant: 'destructive',
    });
  };

  const checkDuplicateAndSubmit = (data: JobFormValues, action: 'draft' | 'publish') => {
    const activeWithSameTitle = (existingJobs || []).some(
      (j) => j.title.toLowerCase().trim() === data.title.toLowerCase().trim() && ['active', 'draft', 'inactive'].includes(j.status)
    );
    if (activeWithSameTitle && !duplicateWarning) {
      setDuplicateWarning(true);
      setPendingAction(action);
      return;
    }
    doSubmit(data, action);
  };

  const submitDraft = () => {
    const data = form.getValues();
    if (!data.title || data.title.trim().length < 2) {
      form.setError('title', { message: t('hire.createJob.jobTitleRequiredForDraft') });
      setStep(0);
      return;
    }
    checkDuplicateAndSubmit(data, 'draft');
  };

  const doSubmit = (data: JobFormValues, action: 'draft' | 'publish') => {
    if (submitInFlightRef.current) return;
    if (maxOpeningsLimit != null && Number(data.number_of_openings) > maxOpeningsLimit) {
      toast({
        title: t('hire.createJob.openingsLimitExceeded'),
        description: t('hire.createJob.maxOpeningsAllowed', {
          count: maxOpeningsLimit,
          level: data.seniority,
        }),
        variant: 'destructive',
      });
      return;
    }

    const { requirements, cv_pass_threshold, interview_pass_threshold, min_experience_years, salary_min, salary_max, open_duration_days, visibility_mode, number_of_openings, ...rest } = data;
    const userId = session?.user?.id;
    const payload = {
      ...rest,
      title: data.title,
      requirements_json: requirements
        ? responsibilities
          ? { text: requirements, responsibilities }
          : { text: requirements }
        : undefined,
      cv_pass_threshold,
      interview_pass_threshold,
      min_experience_years: Number(min_experience_years),
      salary_min: salary_min === '' ? null : Number(salary_min),
      salary_max: salary_max === '' ? null : Number(salary_max),
      notice_period: data.notice_period === '' ? null : String(data.notice_period),
      skills: skills.length > 0 ? skills : [],
      certifications: certifications.length > 0 ? certifications : [],
      languages: languages.length > 0 ? languages : [],
      start_date: data.start_date || null,
      open_duration_days: Number(open_duration_days),
      visibility_mode,
      number_of_openings: Number(number_of_openings),
      status: action === 'publish' ? 'active' : 'draft',
      default_assignee: userId,
    };
    if (action === 'publish') {
      const fields = validateJobForPublish({ ...payload, public_slug: 'pending-slug' });
      if (fields.length > 0) {
        applyPublishValidation(fields);
        return;
      }
    }
    setPublishValidationFields([]);
    const commandKey = publishIdempotencyKey ?? crypto.randomUUID();
    setPublishIdempotencyKey(commandKey);
    persistDraft(form.getValues(), publishDraftJobId, commandKey);
    submitInFlightRef.current = true;
    const mutationInput: Parameters<typeof createJob.mutate>[0] = {
      ...payload,
      existingDraftJobId: publishDraftJobId,
      publishIdempotencyKey: commandKey,
      onDatabaseDraftCreated: (draftId: string) => {
        setPublishDraftJobId(draftId);
        persistDraft(form.getValues(), draftId, commandKey);
      },
    };
    createJob.mutate(mutationInput, {
      onError: (error) => {
        const commandError = error as { resetCommand?: boolean; draftId?: unknown };
        if (commandError.resetCommand === true) {
          setPublishDraftJobId(null);
          setPublishIdempotencyKey(null);
          persistDraft(form.getValues(), null, null);
          submitInFlightRef.current = false;
          return;
        }
        const draftId = commandError.draftId;
        if (typeof draftId === 'string') {
          setPublishDraftJobId(draftId);
          persistDraft(form.getValues(), draftId, commandKey);
        }
        submitInFlightRef.current = false;
      },
      onSuccess: () => {
        submitInFlightRef.current = false;
        setPublishDraftJobId(null);
        safelyClearStoredDraft();
        setOpen(false);
        resetDraftState();
      }
    });
  };


  const jobBlocked = shouldBlockJobCreation(jobEntitlement);
  const publishBlocked = shouldBlockJobPublish(jobEntitlement);

  const handleBlockedClick = useCallback(() => {
    if (jobBlocked || publishBlocked) {
      toast({
        title: t('hire.createJob.jobLimitReached'),
        description: t('hire.createJob.jobLimitReachedDescription', {
          used: jobEntitlement.used,
          limit: jobEntitlement.limit,
        }),
        variant: 'destructive',
      });
    }
  }, [jobBlocked, publishBlocked, jobEntitlement.used, jobEntitlement.limit, t, toast]);

  const handleDialogOpenChange = useCallback((nextOpen: boolean) => {
    if (jobBlocked) return;
    if (nextOpen) {
      setOpen(true);
      return;
    }
    requestClose();
  }, [jobBlocked, requestClose]);

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            {jobBlocked ? (
              <Button
                size="sm"
                className="rounded-full h-10 px-5 press-scale text-sm font-medium opacity-60 cursor-not-allowed"
                aria-disabled="true"
                onClick={handleBlockedClick}
              >
                <Lock className="w-4 h-4 me-1.5" /> {t('hire.dashboard.createJob')}
              </Button>
            ) : (
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-full h-10 px-5 press-scale text-sm font-medium">
                  <Plus className="w-4 h-4 me-1.5" /> {t('hire.dashboard.createJob')}
                </Button>
              </DialogTrigger>
            )}
          </span>
        </TooltipTrigger>
        {jobBlocked && (
          <TooltipContent className="max-w-[250px] text-xs">
            <p>
              {t('hire.createJob.jobLimitTooltip', {
                used: jobEntitlement.used,
                limit: jobEntitlement.limit,
              })}
            </p>
          </TooltipContent>
        )}
      </Tooltip>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t('hire.createJob.title')}</DialogTitle>
          </DialogHeader>
          {restoredDraft && (
            <Alert className="mt-4 border-primary/30 bg-primary/5">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <AlertDescription className="text-sm">
                {t('hire.createJob.draftRestored')}
              </AlertDescription>
            </Alert>
          )}

          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-5">
            {STEP_KEYS.map((s, i) => {
              const isActive = step === i;
              const isInvalid = publishValidationFields.some((field) => field.step === (['role', 'qualifications', 'skills', 'settings'] as const)[i]);
              const isDone = step > i && !isInvalid;
              return (
                <div key={s.id} className="flex items-center gap-2 flex-1">
                  <button
                    type="button"
                    onClick={() => {if (isDone) setStep(i);}}
                    className={cn(
                      "flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold transition-all w-full min-h-[44px]",
                      isActive && "bg-primary text-primary-foreground shadow-md",
                      isInvalid && !isActive && "bg-destructive/10 text-destructive border border-destructive/30",
                      isDone && "bg-primary/10 text-primary cursor-pointer hover:bg-primary/15 border border-primary/20",
                      !isActive && !isDone && "bg-muted/60 text-muted-foreground border border-border"
                    )}>
                    
                    {isDone &&
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                        <span className="text-[10px] text-primary font-bold">✓</span>
                      </div>
                    }
                    {isActive &&
                    <div className="w-5 h-5 rounded-full bg-primary-foreground/20 flex items-center justify-center shrink-0">
                        <span className="text-[10px] text-primary-foreground font-bold">{i + 1}</span>
                      </div>
                    }
                    <span className="truncate">{t(`hire.createJob.${s.key}`)}</span>
                  </button>
                  {i < STEP_KEYS.length - 1 &&
                  <ChevronRight className={cn("w-4 h-4 shrink-0", step > i ? "text-primary" : "text-border")} />
                  }
                </div>);

            })}
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={(e) => e.preventDefault()} className="px-6 pb-6">
            {/* ─── STEP 0: Role ─── */}
            {step === 0 &&
            <div className="space-y-5 animate-in fade-in duration-200">
                {/* Title + AI Generate */}
                <FormField control={form.control} name="title" render={({ field }) =>
              <FormItem>
                     <FormLabel>{t('hire.createJob.jobTitle')} <span className="text-destructive">*</span></FormLabel>
                    <div className="flex gap-2">
                      <FormControl><Input placeholder={t('hire.createJob.jobTitle')} {...field} /></FormControl>
                      <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
                    disabled={aiGenerateBlocked}
                    aria-label={t('hire.createJob.aiGenerateAccessibleName')}
                    title={aiGenerateTitle}
                    onClick={handleAiGenerate}>
                    
                        {!hasAiJobGen ? <Lock className="w-4 h-4" /> : aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        <span className="hidden sm:inline">{aiGenerating ? t('hire.createJob.generating') : t('hire.createJob.aiGenerate')}</span>
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('hire.createJob.aiHint')} {aiGenerateStatusText}
                    </p>
                    <FormMessage />
                  </FormItem>
              } />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField control={form.control} name="department" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.department')} <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input placeholder={t('hire.createJob.departmentPlaceholder')} {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                } />
                  <FormField control={form.control} name="location" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.location')} <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input placeholder={t('hire.createJob.locationPlaceholder')} {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                } />
                </div>

                <div className="grid grid-cols-1 xs:grid-cols-3 gap-4">
                  <FormField control={form.control} name="job_type" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.workModel')} <span className="text-destructive">*</span></FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="onsite">{t('hire.createJob.onsite')}</SelectItem>
                          <SelectItem value="remote">{t('hire.createJob.remote')}</SelectItem>
                          <SelectItem value="hybrid">{t('hire.createJob.hybrid')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                } />
                  <FormField control={form.control} name="employment_type" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.employmentType')} <span className="text-destructive">*</span></FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="full-time">{t('hire.createJob.fullTime')}</SelectItem>
                          <SelectItem value="part-time">{t('hire.createJob.partTime')}</SelectItem>
                          <SelectItem value="contract">{t('hire.createJob.contract')}</SelectItem>
                          <SelectItem value="freelancer">{t('hire.createJob.freelancer')}</SelectItem>
                          <SelectItem value="internship">{t('hire.createJob.internship')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                } />
                  <FormField control={form.control} name="seniority" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.experienceLevel')} <span className="text-destructive">*</span></FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder={t('hire.createJob.select')} /></SelectTrigger></FormControl>
                        <SelectContent>
                           <SelectItem value="tamheer">{t('hire.createJob.tamheer')}</SelectItem>
                           <SelectItem value="intern">{t('hire.createJob.intern')}</SelectItem>
                           <SelectItem value="entry">{t('hire.createJob.entryLevel')}</SelectItem>
                           <SelectItem value="mid">{t('hire.createJob.midLevel')}</SelectItem>
                           <SelectItem value="senior">{t('hire.createJob.senior')}</SelectItem>
                           <SelectItem value="lead">{t('hire.createJob.lead')}</SelectItem>
                           <SelectItem value="manager">{t('hire.createJob.manager')}</SelectItem>
                           <SelectItem value="director">{t('hire.createJob.director')}</SelectItem>
                           <SelectItem value="vp">{t('hire.createJob.vp')}</SelectItem>
                           <SelectItem value="c-level">{t('hire.createJob.cLevel')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                } />
                </div>

                {/* Compensation & Benefits */}
                <div className="rounded-xl border border-border bg-muted/30 p-5 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{t('hire.createJob.compensation')}</p>
                      <p className="text-xs text-muted-foreground">{t('hire.createJob.salaryRange')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 xs:grid-cols-3 gap-3">
                    <FormField control={form.control} name="salary_min" render={({ field }) =>
                  <FormItem>
                        <FormLabel className="text-xs">{t('hire.createJob.minSalary')}</FormLabel>
                        <FormControl><Input type="number" min={0} placeholder="80000" {...field} className="bg-background" /></FormControl>
                      </FormItem>
                  } />
                    <FormField control={form.control} name="salary_max" render={({ field }) =>
                  <FormItem>
                        <FormLabel className="text-xs">{t('hire.createJob.maxSalary')}</FormLabel>
                        <FormControl><Input type="number" min={0} placeholder="120000" {...field} className="bg-background" /></FormControl>
                      </FormItem>
                  } />
                    <FormField control={form.control} name="salary_currency" render={({ field }) =>
                  <FormItem>
                        <FormLabel className="text-xs">{t('hire.createJob.currency')}</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl><SelectTrigger className="bg-background"><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="SAR"><span className="inline-flex items-center gap-1.5">SAR <SarSymbol size={12} /></span></SelectItem>
                            <SelectItem value="AED"><span className="inline-flex items-center gap-1.5">AED <AedSymbol size={12} /></span></SelectItem>
                            <SelectItem value="BHD">BHD (.د.ب)</SelectItem>
                            <SelectItem value="KWD">KWD (د.ك)</SelectItem>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="EUR">EUR (€)</SelectItem>
                            <SelectItem value="GBP">GBP (£)</SelectItem>
                            <SelectItem value="INR">INR (₹)</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                  } />
                  </div>
                  <TagInput
                  label={t('hire.createJob.benefits')}
                  placeholder={t('hire.createJob.addBenefit')}
                  value={benefitInput}
                  onChange={setBenefitInput}
                  onAdd={() => addTag(benefitInput, benefits, setBenefits, setBenefitInput)}
                  tags={benefits}
                  onRemove={(i) => removeTag(i, benefits, setBenefits)}
                  addLabel={t('hire.createJob.add')} />
                
                </div>
              </div>
            }

            {/* ─── STEP 1: Qualifications ─── */}
            {step === 1 &&
            <div className="space-y-5 animate-in fade-in duration-200">
                <FormField control={form.control} name="description" render={({ field }) =>
              <FormItem>
                    <FormLabel>{t('hire.createJob.jobDescription')} <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <Textarea placeholder={t('hire.createJob.descPlaceholder')} rows={4} className="whitespace-pre-wrap" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
              } />
                <div>
                  <label className="text-sm font-medium text-foreground">{t('hire.createJob.responsibilities')} <span className="text-destructive">*</span></label>
                  <Textarea
                  placeholder={t('hire.createJob.respPlaceholder')}
                  rows={5}
                  className={cn("whitespace-pre-wrap mt-1.5", responsibilitiesError && "border-destructive")}
                  value={responsibilities}
                  onChange={(e) => { setResponsibilities(e.target.value); if (responsibilitiesError) setResponsibilitiesError(''); }} />
                
                  {responsibilitiesError ? (
                    <p className="text-[0.8rem] font-medium text-destructive mt-1">{responsibilitiesError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1">{t('hire.createJob.respHint')}</p>
                  )}
                </div>
                <FormField control={form.control} name="requirements" render={({ field }) =>
              <FormItem>
                    <FormLabel>{t('hire.createJob.requirements')} <span className="text-destructive">*</span></FormLabel>
                    <FormControl>
                      <Textarea placeholder={t('hire.createJob.reqPlaceholder')} rows={5} className="whitespace-pre-wrap" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
              } />
              </div>
            }

            {/* ─── STEP 2: Skills & Requirements ─── */}
            {step === 2 &&
            <div className="space-y-5 animate-in fade-in duration-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField control={form.control} name="min_experience_years" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.minExperience')} <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input type="number" min={0} max={50} placeholder="3" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                } />
                  <FormField control={form.control} name="education_level" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.minEducation')} <span className="text-destructive">*</span></FormLabel>
                      <Select value={field.value || ''} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue placeholder={t('hire.createJob.select')} /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="none">{t('hire.createJob.noRequirement')}</SelectItem>
                          <SelectItem value="high-school">{t('hire.createJob.highSchool')}</SelectItem>
                          <SelectItem value="diploma">{t('hire.createJob.diploma')}</SelectItem>
                          <SelectItem value="bachelors">{t('hire.createJob.bachelors')}</SelectItem>
                          <SelectItem value="masters">{t('hire.createJob.masters')}</SelectItem>
                          <SelectItem value="doctorate">{t('hire.createJob.doctorate')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                } />
                </div>

                <TagInput
                label={t('hire.createJob.requiredSkills')}
                placeholder={t('hire.createJob.skillsPlaceholder')}
                value={skillInput}
                onChange={setSkillInput}
                onAdd={() => addTag(skillInput, skills, setSkills, setSkillInput)}
                tags={skills}
                onRemove={(i) => removeTag(i, skills, setSkills)}
                addLabel={t('hire.createJob.add')} />
              
                <TagInput
                label={t('hire.createJob.certifications')}
                placeholder={t('hire.createJob.certPlaceholder')}
                value={certInput}
                onChange={setCertInput}
                onAdd={() => addTag(certInput, certifications, setCertifications, setCertInput)}
                tags={certifications}
                onRemove={(i) => removeTag(i, certifications, setCertifications)}
                addLabel={t('hire.createJob.add')} />
              
                <TagInput
                label={t('hire.createJob.requiredLanguages')}
                placeholder={t('hire.createJob.langPlaceholder')}
                value={langInput}
                onChange={setLangInput}
                onAdd={() => addTag(langInput, languages, setLanguages, setLangInput)}
                tags={languages}
                onRemove={(i) => removeTag(i, languages, setLanguages)}
                addLabel={t('hire.createJob.add')} />
              
              </div>
            }

            {/* ─── STEP 3: Settings ─── */}
            {step === 3 &&
            <div className="space-y-5 animate-in fade-in duration-200">
                <div className="grid grid-cols-1 xs:grid-cols-3 gap-4">
                  <FormField control={form.control} name="number_of_openings" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.numOpenings')} <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input type="number" min={1} max={maxOpeningsLimit ?? 500} placeholder="1" {...field} onChange={(e) => {
                        const nextValue = Number(e.target.value);
                        if (maxOpeningsLimit != null && nextValue > maxOpeningsLimit) {
                          field.onChange(maxOpeningsLimit);
                          return;
                        }
                        field.onChange(e.target.value);
                      }} /></FormControl>
                      {maxOpeningsLimit != null ? (
                        <p className="text-[11px] text-amber-600 font-medium">
                          {t('hire.createJob.openingsLimitHint', { count: maxOpeningsLimit, level: selectedSeniority })}
                        </p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">{t('hire.createJob.positionsToFill')}</p>
                      )}
                      <FormMessage />
                    </FormItem>
                } />
                  <FormField control={form.control} name="notice_period" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.noticePeriod')}</FormLabel>
                      <FormControl><Input type="number" min={1} step={1} placeholder="30" {...field} onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))} /></FormControl>
                      <FormMessage />
                    </FormItem>
                } />
                  <FormField control={form.control} name="start_date" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.startDate')}</FormLabel>
                      <FormControl><Input type="date" {...field} /></FormControl>
                    </FormItem>
                } />
                </div>

                <div className="grid grid-cols-1 xs:grid-cols-3 gap-4">
                  <FormField control={form.control} name="cv_pass_threshold" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.cvPassScore')} <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input type="number" min={0} max={100} placeholder="80" {...field} /></FormControl>
                      <p className="text-[11px] text-muted-foreground">{t('hire.createJob.cvPassHint')}</p>
                      <FormMessage />
                    </FormItem>
                } />
                  <FormField control={form.control} name="interview_pass_threshold" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.interviewPassScore')} <span className="text-destructive">*</span></FormLabel>
                      <FormControl><Input type="number" min={0} max={100} placeholder="70" {...field} /></FormControl>
                      <p className="text-[11px] text-muted-foreground">{t('hire.createJob.interviewPassHint')}</p>
                      <FormMessage />
                    </FormItem>
                } />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField control={form.control} name="open_duration_days" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.postingDuration')} <span className="text-destructive">*</span></FormLabel>
                      <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="14">{t('hire.createJob.days14')}</SelectItem>
                          <SelectItem value="30">{t('hire.createJob.days30')}</SelectItem>
                          <SelectItem value="45">{t('hire.createJob.days45')}</SelectItem>
                          <SelectItem value="60">{t('hire.createJob.days60')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                } />
                </div>

                <div className="grid grid-cols-1 xs:grid-cols-3 gap-4">
                  <FormField control={form.control} name="visibility_mode" render={({ field }) =>
                <FormItem>
                      <FormLabel>{t('hire.createJob.visibility')} <span className="text-destructive">*</span></FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="direct_link_only">{t('hire.createJob.linkOnly')}</SelectItem>
                          <SelectItem value="seek_public">{t('hire.createJob.public')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                } />
                </div>

                {duplicateWarning &&
              <Alert className="border-warning/30 bg-warning/5">
                    <AlertTriangle className="h-4 w-4 text-warning" />
                    <AlertDescription className="text-sm">
                      {t('hire.createJob.duplicateWarning')}
                      <div className="flex gap-2 mt-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => {setDuplicateWarning(false);setPendingAction(null);}}>{t('hire.createJob.cancel')}</Button>
                        <Button type="button" size="sm" className="bg-warning text-warning-foreground hover:bg-warning/90" onClick={() => {
                      if (pendingAction) form.handleSubmit((data) => doSubmit(data, pendingAction))();
                    }}>{t('hire.createJob.yesProceed')}</Button>
                      </div>
                    </AlertDescription>
                  </Alert>
              }
              </div>
            }

            {/* Navigation footer */}
            <div className="flex items-center justify-between pt-5 mt-5 border-t border-border">
              <div>
                {step > 0 &&
                <Button type="button" variant="ghost" size="sm" onClick={goBack} className="gap-1">
                    <ChevronLeft className="w-4 h-4 rtl-flip" /> {t('hire.createJob.back')}
                  </Button>
                }
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={requestClose}>{t('hire.createJob.cancel')}</Button>
                {step < 3 ?
                <Button type="button" size="sm" onClick={goNext} className="gradient-accent text-accent-foreground gap-1">
                    {t('hire.createJob.next')} <ChevronRight className="w-4 h-4 rtl-flip" />
                  </Button> :

                <>
                    <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={createJob.isPending}
                    onClick={submitDraft}>
                    
                      {createJob.isPending ? t('hire.createJob.saving') : t('hire.createJob.saveDraft')}
                    </Button>
                    <Button
                    type="button"
                    size="sm"
                    disabled={createJob.isPending}
                    aria-disabled={publishBlocked}
                    className={cn(
                      "gradient-accent text-accent-foreground",
                      publishBlocked && "opacity-60 cursor-not-allowed",
                    )}
                    onClick={publishBlocked
                      ? handleBlockedClick
                      : () => form.handleSubmit((data) => checkDuplicateAndSubmit(data, 'publish'))()}>
                    
                      {createJob.isPending ? t('hire.createJob.publishing') : t('hire.createJob.publishJob')}
                    </Button>
                  </>
                }
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('hire.createJob.discardDraftTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('hire.createJob.discardDraftDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('hire.createJob.keepEditing')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmDiscardDraft}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t('hire.createJob.discardDraft')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>);

}
